/**
 * space_front.ts — THE FRONT, named twice and never confused (space campaign B, 2026-09-30; report §4.3, space.md §14
 * gap 22). Pure.
 *
 * Every hour's war has TWO places and the ops panel used to print a third:
 *   · the PHYSICAL BATTLEFIELD — the fort in the Commons Reach (sys0) where the lines actually fight: `fortOf(cyc)`;
 *   · the STRATEGIC FRONT — the system whose control the war decides: the live `space_campaign.targetSystem` row (the
 *     pressing council's target, else the cycle's rotation the server chose when it opened the campaign). Before a
 *     cycle's row exists it is a FORECAST: a council's `press` operation for that cycle if one stands, else the cycle
 *     hash — and it says so.
 * The ops panel printed `warTargetOf(cycle)` (the hash) even when a council had pressed another system, so it named a
 * different front than the HUD, the campaign and the receipts. Every label now comes from here.
 *
 * What changes if a side wins: the winning side's receipts pay credits and the front's `sector_control` moves by
 * CAMPAIGN_SOV_SHIFT. A single kill does not move the front (the per-kill `warContribution` path is retired — slice C).
 */
import { fortOf, warTargetOf, CAMPAIGN_SOV_SHIFT, BATTLE_PERIOD_S, BATTLE_LEN_S } from '@shared/space_campaign.js';

export interface FrontCampaign { cyc: number; fortKey: string; targetSystem: string; state: string; winner: string }
export interface FrontPress { civ: string; cyc: number; targetSystem: string; /** the press utility (the stronger press names the front) */ press: number }

export interface FrontFacts {
  nowS: number;
  /** The replicated campaign rows (the one of the cycle this reads is picked here; none yet = a forecast). */
  campaigns: readonly FrontCampaign[];
  /** The councils' `press` operations (any cycle — filtered here). */
  presses: readonly FrontPress[];
  /** A standing ceasefire covers cycle `cyc`. */
  ceasefireFor: (cyc: number) => boolean;
  /** Names a system id (null = unknown). */
  systemName: (id: string) => string | null;
}

export interface FrontLabels {
  cyc: number;
  live: boolean;
  /** "Redhand Fortress, in the Commons Reach (sys0)". */
  battlefield: string;
  /** "war on — 12:30 left" | "next war in 41:05" | "ceasefire". */
  when: string;
  frontSystem: string;
  frontName: string;
  /** How the front was chosen: the live row / a council press forecast / the cycle rotation forecast. */
  source: 'campaign' | 'council' | 'rotation';
  /** "Kessway (sys42) — the Concord council pressed it". */
  front: string;
  /** What winning changes. */
  stakes: string;
}

const CIV_NAME: Record<string, string> = { aurelian: 'Concord', borean: 'Compact' };
const mmss = (s: number): string => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.floor(Math.max(0, s) % 60)).padStart(2, '0')}`;

/** "Kessway (sys42)" — a system with its id, or the id alone. */
export function systemLabel(id: string, name: string | null): string { return name && name !== id ? `${name} (${id})` : id; }

export function frontLabels(f: FrontFacts): FrontLabels {
  const cycNow = Math.floor(f.nowS / BATTLE_PERIOD_S);
  const t = f.nowS - cycNow * BATTLE_PERIOD_S;
  const live = t <= BATTLE_LEN_S && !f.ceasefireFor(cycNow);
  const cyc = live ? cycNow : cycNow + 1;
  const ceasefire = !live && f.ceasefireFor(cyc);
  const fort = fortOf(cyc);
  const battlefield = `${fort.name}, in the Commons Reach (sys0)`;
  const when = ceasefire ? 'ceasefire — the next war will not open' : live ? `war on — ${mmss(BATTLE_LEN_S - t)} left` : `next war in ${mmss((cycNow + 1) * BATTLE_PERIOD_S - f.nowS)}`;
  const row = f.campaigns.find((c) => c.cyc === cyc) ?? null;
  const presses = f.presses.filter((p) => p.cyc === cyc && p.targetSystem);
  const presser = presses.length ? presses.reduce((a, b) => (b.press > a.press ? b : a)) : null;
  let frontSystem: string, source: FrontLabels['source'], why: string;
  if (row) {
    frontSystem = row.targetSystem;
    const pressed = presses.find((p) => p.targetSystem === row.targetSystem);
    source = 'campaign';
    why = pressed ? `the ${CIV_NAME[pressed.civ] ?? pressed.civ} council pressed it` : 'this cycle\'s rotation (no council pressed)';
  } else if (presser) {
    frontSystem = presser.targetSystem; source = 'council';
    why = `forecast — the ${CIV_NAME[presser.civ] ?? presser.civ} council is pressing it`;
  } else {
    frontSystem = warTargetOf(cyc); source = 'rotation';
    why = 'forecast — this cycle\'s rotation, unless a council presses another';
  }
  const frontName = f.systemName(frontSystem) ?? frontSystem;
  return {
    cyc, live, battlefield, when, frontSystem, frontName, source,
    front: `${systemLabel(frontSystem, frontName)} — ${why}`,
    stakes: `The winner's receipts pay credits and move control of ${frontName} by ${CAMPAIGN_SOV_SHIFT}; a single kill does not move the front.`,
  };
}
